'use client';

import { useQuery } from '@tanstack/react-query';

import { getQueryClient } from 'app/get-query-client';
import { exchangeApiV1 } from 'lib/api/exchangeApi';
import { useAuthStore } from 'lib/stores/authStore';

/**
 * Information compliance, risk or support has asked the user to send in before
 * they keep using the app. The server only ever returns requests that are still
 * open, so an empty list means there is nothing to do.
 */
export type InfoRequestItemType = 'address' | 'ssn' | 'phone' | 'proof_of_address' | 'source_of_funds';

export type InfoRequestItemStatus = 'requested' | 'received' | 'satisfied' | 'rejected' | 'waived';

/** Profile fields are satisfied by the existing settings writes; documents by an upload. */
export const PROFILE_FIELD_ITEM_TYPES = ['address', 'ssn', 'phone'] as const;
export type ProfileFieldItemType = (typeof PROFILE_FIELD_ITEM_TYPES)[number];

export const DOCUMENT_ITEM_TYPES = ['proof_of_address', 'source_of_funds'] as const;
export type DocumentItemType = (typeof DOCUMENT_ITEM_TYPES)[number];

export interface InfoRequestItem {
    item_id: string;
    item_type: InfoRequestItemType;
    status: InfoRequestItemStatus;
    review_note?: string;
    waived_reason?: string;
}

export interface InfoRequest {
    request_id: string;
    team: string;
    enforcement: 'advisory' | 'blocking';
    status: string;
    message?: string;
    due_ts?: string;
    created_ts: string;
    items: InfoRequestItem[];
}

interface GetInfoRequestsResponse {
    requests: InfoRequest[];
}

export interface CreateInfoRequestUploadUrlResponse {
    upload_id: string;
    upload_url: string;
    content_type: string;
    expires_ts: string;
}

export const infoRequestKeys = {
    all: ['infoRequests'] as const,
    user: (userId: string) => [...infoRequestKeys.all, userId] as const,
};

function isProfileFieldItem(itemType: InfoRequestItemType): itemType is ProfileFieldItemType {
    return (PROFILE_FIELD_ITEM_TYPES as readonly string[]).includes(itemType);
}

function isDocumentItem(itemType: InfoRequestItemType): itemType is DocumentItemType {
    return (DOCUMENT_ITEM_TYPES as readonly string[]).includes(itemType);
}

async function fetchInfoRequests(userId: string, signal?: AbortSignal): Promise<InfoRequest[]> {
    const response = await exchangeApiV1.get<GetInfoRequestsResponse>(`/users/${userId}/info_requests`, { signal });
    return response.data.requests ?? [];
}

/**
 * Fetched once per session (`staleTime: Infinity`) and refetched explicitly after
 * each completed step — the server's view is the only thing that says whether a
 * request is satisfied.
 */
export function useInfoRequestsQuery() {
    const userId = useAuthStore((s) => s.userId) ?? '';
    const isLoggedInMember = useAuthStore((s) => s.isLoggedInMember);

    return useQuery({
        queryKey: infoRequestKeys.user(userId),
        queryFn: ({ signal }) => fetchInfoRequests(userId, signal),
        enabled: !!userId && isLoggedInMember,
        staleTime: Infinity,
    });
}

/** Re-pulls the server's list; used after a submit to learn whether the ask actually cleared. */
export function refetchInfoRequests(): Promise<InfoRequest[]> {
    const userId = useAuthStore.getState().userId ?? '';
    return getQueryClient().fetchQuery({
        queryKey: infoRequestKeys.user(userId),
        queryFn: ({ signal }) => fetchInfoRequests(userId, signal),
        staleTime: 0,
    });
}

export interface BlockingItem {
    itemId: string;
    itemType: InfoRequestItemType;
    message?: string;
}

/**
 * Everything the user still owes on a request the operator marked blocking,
 * profile fields and documents alike. Unknown item types are dropped rather
 * than surfaced: a type this client has no step for would otherwise hold the
 * blocking modal open with nothing to render.
 */
export function selectBlockingItems(requests: InfoRequest[] | undefined): BlockingItem[] {
    const items: BlockingItem[] = [];
    for (const request of requests ?? []) {
        if (request.enforcement !== 'blocking') continue;
        for (const item of request.items) {
            if (item.status !== 'requested') continue;
            if (!isProfileFieldItem(item.item_type) && !isDocumentItem(item.item_type)) continue;
            items.push({ itemId: item.item_id, itemType: item.item_type, message: request.message });
        }
    }
    return items;
}

export interface DocumentRequestItem {
    itemId: string;
    itemType: DocumentItemType;
    status: InfoRequestItemStatus;
    reviewNote?: string;
    message?: string;
}

/** Outstanding document asks from non-blocking requests; blocking ones run in the modal. */
export function selectDocumentItems(requests: InfoRequest[] | undefined): DocumentRequestItem[] {
    const items: DocumentRequestItem[] = [];
    for (const request of requests ?? []) {
        if (request.enforcement === 'blocking') continue;
        for (const item of request.items) {
            if (!isDocumentItem(item.item_type)) continue;
            if (item.status !== 'requested' && item.status !== 'received') continue;
            items.push({
                itemId: item.item_id,
                itemType: item.item_type,
                status: item.status,
                reviewNote: item.review_note,
                message: request.message,
            });
        }
    }
    return items;
}

/**
 * Browsers on some platforms report an empty `File.type` for HEIC. Sending
 * `application/octet-stream` in that case would be rejected by the server, which
 * blocks that type outright, so fall back to the extension before the generic
 * type — the picker already restricts what can be chosen.
 */
const EXTENSION_CONTENT_TYPES: Record<string, string> = {
    heic: 'image/heic',
    heif: 'image/heif',
    jpeg: 'image/jpeg',
    jpg: 'image/jpeg',
    pdf: 'application/pdf',
    png: 'image/png',
};

export function resolveContentType(file: File): string {
    if (file.type) return file.type;
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    return EXTENSION_CONTENT_TYPES[extension] ?? 'application/octet-stream';
}

export async function createInfoRequestUploadUrl(
    userId: string,
    itemId: string,
    file: File,
): Promise<CreateInfoRequestUploadUrlResponse> {
    const response = await exchangeApiV1.post<CreateInfoRequestUploadUrlResponse>(
        `/users/${userId}/info_requests/upload_url`,
        {
            item_id: itemId,
            content_type: resolveContentType(file),
            original_file_name: file.name,
            size_bytes: file.size,
        },
    );
    return response.data;
}

/** Returns the request as the server now sees it, so callers can trust it without a refetch. */
export async function submitInfoRequestItem(userId: string, itemId: string, uploadIds: string[]): Promise<InfoRequest> {
    const response = await exchangeApiV1.post<{ request: InfoRequest }>(
        `/users/${userId}/info_requests/items/${itemId}/submit`,
        { upload_ids: uploadIds },
    );
    return response.data.request;
}

/** The cache as it stands, for callers that cannot afford a failed refetch. */
export function readCachedInfoRequests(): InfoRequest[] | undefined {
    const userId = useAuthStore.getState().userId ?? '';
    return getQueryClient().getQueryData<InfoRequest[]>(infoRequestKeys.user(userId));
}

/**
 * Folds a server-confirmed request into the cache. The blocking modal has no
 * close button, so it must not depend on a follow-up GET succeeding to learn
 * that a submit landed.
 */
export function applyInfoRequestUpdate(updated: InfoRequest): void {
    const userId = useAuthStore.getState().userId ?? '';
    getQueryClient().setQueryData<InfoRequest[]>(infoRequestKeys.user(userId), (previous) =>
        (previous ?? []).map((request) => (request.request_id === updated.request_id ? updated : request)),
    );
}
